import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, Clock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const FORMSPREE_URL = 'https://formspree.io/f/mykqrrgn';

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    careNeeded: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '',
          care_needed: formData.careNeeded || '',
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', careNeeded: '', message: '' });
      } else {
        const data = await response.json();
        setSubmitStatus('error');
        setErrorMessage(data?.errors?.[0]?.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Unable to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+1 (608) 358-9578',
      href: 'tel:+16083589578',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'info@heritagehhealth.com',
      href: 'mailto:info@heritagehhealth.com',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: '4614 Crescent Rd\nFitchburg, WI 53711',
      href: null,
    },
    {
      icon: Clock,
      label: 'Hours',
      value: 'Available 24/7 for care\nOffice: Mon-Fri 8am-6pm',
      href: null,
    },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-28 lg:py-32 bg-warm-beige overflow-hidden" data-testid="contact-form-section">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-72 h-72 sm:w-[420px] sm:h-[420px] md:w-[500px] md:h-[500px] bg-gradient-to-bl from-soft-gold/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] bg-gradient-to-tr from-deep-blue/5 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-12 lg:gap-20">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
              Get In Touch
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-navy mb-5 sm:mb-6">
              Contact{' '}
              <span className="text-gradient-gold">Heritage Home Health LLC</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 sm:mb-10">
              We're here to answer your questions and help you explore the best care
              options for your loved ones. Reach out today for a free, no-obligation consultation.
            </p>

            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                const Content = (
                  <div className="flex items-start space-x-3 sm:space-x-4 group">
                    <div className="w-12 h-12 rounded-xl bg-soft-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-soft-gold/20 transition-colors">
                      <Icon className="w-5 h-5 text-soft-gold" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-500 mb-1">{item.label}</p>
                      <p className="text-navy font-medium whitespace-pre-line break-words">{item.value}</p>
                    </div>
                  </div>
                );

                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                  >
                    {item.href ? (
                      <a href={item.href} className="block hover:opacity-80 transition-opacity">
                        {Content}
                      </a>
                    ) : (
                      Content
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="glass-strong shadow-soft-3d-lg rounded-3xl p-5 sm:p-7 md:p-10">
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8 sm:py-12"
                >
                  <div className="w-20 h-20 rounded-full bg-muted-green/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-muted-green" />
                  </div>
                  <h3 className="font-serif text-2xl text-navy mb-3">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for contacting Heritage Home Health LLC.
                    We'll get back to you shortly.
                  </p>
                  <Button
                    onClick={() => setSubmitStatus(null)}
                    variant="outline"
                    className="rounded-full w-full sm:w-auto"
                  >
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-testid="contact-form">
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-800 font-medium">Unable to send message</p>
                        <p className="text-red-600 text-sm">{errorMessage}</p>
                      </div>
                    </motion.div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-navy font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        disabled={isSubmitting}
                        className="bg-white/50 backdrop-blur border-soft-gray focus:border-soft-gold focus:ring-soft-gold rounded-xl h-11 sm:h-12"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-navy font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        disabled={isSubmitting}
                        className="bg-white/50 backdrop-blur border-soft-gray focus:border-soft-gold focus:ring-soft-gold rounded-xl h-11 sm:h-12"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-navy font-medium">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 000-0000"
                        disabled={isSubmitting}
                        className="bg-white/50 backdrop-blur border-soft-gray focus:border-soft-gold focus:ring-soft-gold rounded-xl h-11 sm:h-12"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="careNeeded" className="text-navy font-medium">
                        Type of Care Needed
                      </Label>
                      <Input
                        id="careNeeded"
                        name="careNeeded"
                        value={formData.careNeeded}
                        onChange={handleChange}
                        placeholder="e.g., Personal Care, Companionship"
                        disabled={isSubmitting}
                        className="bg-white/50 backdrop-blur border-soft-gray focus:border-soft-gold focus:ring-soft-gold rounded-xl h-11 sm:h-12"
                        data-testid="contact-care-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-navy font-medium">
                      Tell Us About Your Needs *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please share any details about the care you're looking for..."
                      rows={5}
                      required
                      disabled={isSubmitting}
                      className="bg-white/50 backdrop-blur border-soft-gray focus:border-soft-gold focus:ring-soft-gold rounded-xl resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="w-full bg-navy text-white hover:bg-deep-blue rounded-full h-12 sm:h-14 text-sm sm:text-base font-semibold shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        <span className="sm:hidden">Send Message</span>
                        <span className="hidden sm:inline">Send Message to Heritage Home Health LLC</span>
                      </>
                    )}
                  </Button>

                  <p className="text-center text-sm text-gray-500">
                    By submitting this form, you agree to be contacted by Heritage Home Health LLC.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
